import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as icons from '@codegrade/icons';
import * as Form from '@/components/Form';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
/** A styled text input for search functionality, with a search icon and a clear button. */
export default tsx.componentFactoryOf().create({
    name: 'search-input',
    props: {
        /** The current search query. Can be bound with `v-model` or `.sync`. */
        query: p.ofType().required,
        /** The name of the resource being searched (e.g., "Course", "Submission"). Used for ARIA labels. */
        resource: p.ofType().required,
        /** The name of the resource being searched in plural form, default to `${resource}s`. */
        resources: p.ofType().optional,
        /** The ID of the element that the search input controls, for accessibility. */
        ariaControls: p.ofType().optional,
        /** Placeholder text for the input. */
        placeholder: p(String).default('Type to search'),
        /** Adds a v-focus directive on the input element. */
        autoFocus: p(Boolean).default(false),
    },
    computed: {
        /** @returns The VNode for the search icon. */
        iconNode() {
            return <Icon icon={icons.Search} class={this.iconClasses}/>;
        },
        /** @returns The final category noun plural form. */
        finalCategoryPluralForm() {
            return this.resources ?? `${this.resource}s`;
        },
        /** @returns An array of CSS classes for the search icon. */
        iconClasses() {
            return [
                'absolute',
                'left-3',
                'top-1/2',
                'transform',
                '-translate-y-1/2',
                'size-4',
                'pointer-events-none',
                'text-brand-40',
                'dark:text-brand-20',
            ];
        },
        /** @returns An array of CSS classes for the `<input>` element. */
        inputClasses() {
            return ['w-full', 'm-0', 'pl-10', 'pr-10', 'cg-form-control'];
        },
        /** @returns An array of CSS classes for the clear button. */
        buttonClasses() {
            return [
                'absolute',
                'right-2',
                'top-1/2',
                'transform',
                '-translate-y-1/2',
                'appearance-none',
                'bg-transparent',
                'border-0',
                'btn',
                'btn-sm',
                'size-6',
                'focus:outline-none',
            ];
        },
        /** @returns The VNode for the entire search input component, including the icon and clear button. */
        inputNode() {
            const query = this.query ?? '';
            return (<div class="relative">
                    {this.iconNode}
                    <Form.Input type="text" data-cy={`search-input-${this.resource.toLocaleLowerCase()}`} class={this.inputClasses} placeholder={this.placeholder} value={query} aria-controls={this.ariaControls} onInput={event => {
                    let newQuery = event;
                    if (newQuery === '') {
                        newQuery = null;
                    }
                    tsx.emitOn(this, 'onInput', { query: newQuery });
                }} v-focus={this.autoFocus} aria-label={`Search ${this.resource}s`}/>

                    {utils.ifOrEmpty(query.length > 0, () => (<button type="button" class={this.buttonClasses} onClick={() => {
                        tsx.emitOn(this, 'onInput', { query: null });
                    }} aria-label="Clear search">
                            <Icon icon={icons.Close} class="size-4"/>
                        </button>))}
                </div>);
        },
    },
    /**
     * @returns The VNode for the component.
     */
    render() {
        return this.inputNode;
    },
});
